import { useCallback, useEffect, useState } from 'react'
import { FileText, Calendar, Loader2, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { getWeeklyReports, getWeeklyReportContent, type WeeklyReportSummary } from '@/api/adminService'
import ReportViewer from './ReportViewer'

/** Return YYYY-MM-DD for N days ago. */
function daysAgo(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function ReportsPage() {
  const [reports, setReports] = useState<WeeklyReportSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [dateFrom, setDateFrom] = useState(daysAgo(30))
  const [dateTo, setDateTo] = useState(today())

  const [selected, setSelected] = useState<string | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [contentLoading, setContentLoading] = useState(false)

  const fetchReports = useCallback(() => {
    setLoading(true)
    setError(null)
    getWeeklyReports({ date_from: dateFrom, date_to: dateTo })
      .then((data) => {
        setReports(data)
        // Auto-select first report, or clear selection if no reports
        setSelected(data.length > 0 ? data[0].filename : null)
      })
      .catch(() => setError('Failed to load reports'))
      .finally(() => setLoading(false))
  }, [dateFrom, dateTo])

  // Fetch on mount
  useEffect(() => { fetchReports() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch report content when selection changes
  useEffect(() => {
    if (!selected) {
      setContent(null)
      return
    }
    setContentLoading(true)
    getWeeklyReportContent(selected)
      .then(setContent)
      .catch(() => setContent('Failed to load report content.'))
      .finally(() => setContentLoading(false))
  }, [selected])

  // Group reports by date
  const grouped = reports.reduce<Record<string, WeeklyReportSummary[]>>((acc, r) => {
    ;(acc[r.date] ??= []).push(r)
    return acc
  }, {})

  return (
    <div className="flex gap-6 min-h-[600px]">
      {/* Left: date filter + report list */}
      <div className="w-72 shrink-0 flex flex-col bg-white rounded-lg border border-gray-200 max-h-[calc(100vh-200px)]">
        {/* Date range filter */}
        <div className="px-4 py-3 border-b border-gray-200 space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">Weekly Reports</h3>
          <div className="space-y-1.5">
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded px-2 py-1 text-gray-700"
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded px-2 py-1 text-gray-700"
              />
            </div>
          </div>
          <button
            onClick={fetchReports}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded px-3 py-1.5 transition-colors"
          >
            <Search className="w-3 h-3" />
            Filter
          </button>
        </div>

        {/* Report list */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <p className="text-red-500 text-xs py-6 text-center">{error}</p>
          ) : reports.length === 0 ? (
            <p className="text-gray-400 text-xs py-6 text-center">No reports in this range.</p>
          ) : (
            Object.entries(grouped).map(([date, items]) => (
              <div key={date}>
                <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">{date}</span>
                </div>
                {items.map((report) => (
                  <button
                    key={report.filename}
                    onClick={() => setSelected(report.filename)}
                    className={clsx(
                      'w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors',
                      selected === report.filename
                        ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-600'
                        : 'hover:bg-gray-50 text-gray-700 border-l-2 border-transparent',
                    )}
                  >
                    <FileText className="w-4 h-4 shrink-0" />
                    <span className="capitalize">{report.report_type}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right: report content */}
      <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden max-h-[calc(100vh-200px)]">
        {contentLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : content ? (
          <div className="overflow-y-auto h-full">
            <ReportViewer markdown={content} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            {reports.length === 0 ? 'No reports found' : 'Select a report to view'}
          </div>
        )}
      </div>
    </div>
  )
}
